import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Table,
  Tag,
  Icon,
  DatePicker,
  Progress
} from "antd";
import moment from "moment";

const { RangePicker } = DatePicker;

const AuditTrailSummaryView = ({
  summaryData,
  startDate,
  endDate,
  onRefresh,
  onDateRangeChange
}) => {
  if (!summaryData) {
    return (
      <div className="text-center p-5">
        <p>Loading summary data...</p>
      </div>
    );
  }

  const {
    statistics,
    events_by_type: eventsType,
    top_users: topUsers,
    busiest_hours: busiestHours,
    date_range: dateRange,
    generated_at: generatedAt
  } = summaryData;

  // Calculate success rate
  const successRate =
    statistics.total_events > 0
      ? (
          (statistics.successful_events / statistics.total_events) *
          100
        ).toFixed(1)
      : 0;

  // Format event types for display
  const eventTypesData = Object.entries(eventsType || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([key, value]) => ({
      event: key,
      count: value,
      percentage: ((value / statistics.total_events) * 100).toFixed(1)
    }));

  // Format top users for display
  const topUsersData = Object.entries(topUsers || {})
    .sort(([, a], [, b]) => b - a)
    .map(([name, count]) => ({
      name,
      count,
      percentage: ((count / statistics.total_events) * 100).toFixed(1)
    }));

  // Format busiest hours
  const busiestHoursData = Object.entries(busiestHours || {})
    .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
    .map(([hour, count]) => ({
      hour: `${parseInt(hour, 10)}:00`,
      count,
      percentage: ((count / statistics.total_events) * 100).toFixed(1)
    }));

  const eventColumns = [
    {
      title: "Event Type",
      dataIndex: "event",
      key: "event",
      render: text => <Tag color="blue">{text}</Tag>
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "right"
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      render: text => `${text}%`
    }
  ];

  const userColumns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: text => (
        <div>
          <Icon type="user" style={{ marginRight: 8 }} />
          {text}
        </div>
      )
    },
    {
      title: "Events",
      dataIndex: "count",
      key: "count",
      align: "right"
    },
    {
      title: "Activity",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      render: text => `${text}%`
    }
  ];

  const hourColumns = [
    {
      title: "Hour",
      dataIndex: "hour",
      key: "hour"
    },
    {
      title: "Events",
      dataIndex: "count",
      key: "count",
      align: "right"
    },
    {
      title: "Activity",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      render: percentage => (
        <Progress
          percent={parseFloat(percentage)}
          size="small"
          format={percent => `${percent}%`}
        />
      )
    }
  ];

  const handleDateRangeChange = dates => {
    if (dates && dates.length === 2) {
      const start = dates[0].format("YYYY-MM-DD");
      const end = dates[1].format("YYYY-MM-DD");
      onDateRangeChange(start, end);
    }
  };

  return (
    <div className="audit-trail-summary">
      {/* Header Section */}
      <Row gutter={16} className="mb-4">
        <Col span={12}>
          <h3 style={{ marginBottom: 0 }}>Audit Trail Summary</h3>
          <p style={{ color: "#666", marginTop: 4 }}>
            Period: {moment(dateRange?.start).format("MMM DD, YYYY")} -{" "}
            {moment(dateRange?.end).format("MMM DD, YYYY")}
          </p>
        </Col>
        <Col span={12} className="text-right">
          <RangePicker
            value={[moment(startDate), moment(endDate)]}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            className="mr-3"
          />
          <Button icon="reload" onClick={onRefresh}>
            Refresh
          </Button>
          <div style={{ fontSize: "12px", color: "#999", marginTop: 4 }}>
            Generated: {moment(generatedAt).format("MMM DD, YYYY HH:mm")}
          </div>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={16} className="mb-4">
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Events"
              value={statistics.total_events}
              valueStyle={{ color: "#1890ff" }}
              prefix={<Icon type="bar-chart" />}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
              Daily Average: {statistics.daily_average}
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={successRate}
              valueStyle={{ color: "#52c41a" }}
              suffix="%"
              prefix={<Icon type="check-circle" />}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
              {statistics.successful_events} successful
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Failed Events"
              value={statistics.failed_events}
              valueStyle={{
                color: statistics.failed_events > 0 ? "#ff4d4f" : "#52c41a"
              }}
              prefix={<Icon type="close-circle" />}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
              Failed Logins: {statistics.failed_logins}
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Patient Exports"
              value={statistics.patient_exports}
              valueStyle={{ color: "#722ed1" }}
              prefix={<Icon type="download" />}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: 8 }}>
              Export operations
            </div>
          </Card>
        </Col>
      </Row>

      {/* Event Types and Users */}
      <Row gutter={16} className="mb-4">
        <Col span={12}>
          <Card
            title="Top Event Types"
            size="small"
            extra={
              <span style={{ fontSize: "12px", color: "#666" }}>
                {Object.keys(eventsType || {}).length} types total
              </span>
            }
          >
            <Table
              dataSource={eventTypesData}
              columns={eventColumns}
              pagination={false}
              size="small"
              rowKey="event"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card
            title="Most Active Users"
            size="small"
            extra={
              <span style={{ fontSize: "12px", color: "#666" }}>
                {Object.keys(topUsers || {}).length} users total
              </span>
            }
          >
            <Table
              dataSource={topUsersData}
              columns={userColumns}
              pagination={false}
              size="small"
              rowKey="name"
            />
          </Card>
        </Col>
      </Row>

      {/* Peak Hours */}
      <Row gutter={16}>
        <Col span={24}>
          <Card
            title="Peak Activity Hours"
            size="small"
            extra={
              <span style={{ fontSize: "12px", color: "#666" }}>
                24-hour format
              </span>
            }
          >
            <Table
              dataSource={busiestHoursData}
              columns={hourColumns}
              pagination={false}
              size="small"
              rowKey="hour"
              scroll={{ y: 300 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

AuditTrailSummaryView.propTypes = {
  summaryData: PropTypes.shape({
    statistics: PropTypes.shape({
      total_events: PropTypes.number,
      successful_events: PropTypes.number,
      failed_events: PropTypes.number,
      failed_logins: PropTypes.number,
      patient_exports: PropTypes.number,
      daily_average: PropTypes.number
    }),
    events_by_type: PropTypes.objectOf(PropTypes.number),
    events_by_status: PropTypes.objectOf(PropTypes.number),
    top_users: PropTypes.objectOf(PropTypes.number),
    busiest_hours: PropTypes.objectOf(PropTypes.number),
    date_range: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string
    }),
    generated_at: PropTypes.string
  }),
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired
};

AuditTrailSummaryView.defaultProps = {
  summaryData: null
};

export default AuditTrailSummaryView;
